/**
 * Popover — a portaled, fixed-positioned floating panel anchored to a trigger
 * element. It renders into `document.body` (via a portal) and positions itself
 * with `position: fixed` computed from the anchor's viewport rect, so it
 * ESCAPES the overflow clipping of modals and scrolling tables (the hard
 * requirement in the Z1 brief: "pickers/popovers must be portaled /
 * fixed-positioned so they escape modal + table overflow clipping").
 *
 * Dismissal: outside-click (pointerdown outside both anchor and panel) and
 * Escape. Repositions on scroll/resize while open. Flips above the anchor when
 * there isn't room below.
 */
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode, type RefObject } from "react";
import { createPortal } from "react-dom";

export interface PopoverProps {
  /** The trigger the panel anchors to. */
  anchorRef: RefObject<HTMLElement>;
  open: boolean;
  onClose: () => void;
  /** Panel content. */
  children: ReactNode;
  /** Min width; defaults to matching the anchor's width. */
  minWidth?: number;
  className?: string;
  /** Accessible role for the panel (listbox for pickers, dialog otherwise). */
  role?: string;
  "aria-label"?: string;
}

interface Coords {
  left: number;
  top: number;
  minWidth: number;
  /** placed above the anchor (flipped) — lets callers style if needed */
  flipped: boolean;
}

const GAP = 4;
const EST_PANEL_H = 300; // matches .picker-pop max-height

function computeCoords(anchor: HTMLElement, minWidthProp?: number): Coords {
  const r = anchor.getBoundingClientRect();
  const spaceBelow = window.innerHeight - r.bottom;
  const flip = spaceBelow < EST_PANEL_H && r.top > spaceBelow;
  return {
    left: Math.max(8, Math.min(r.left, window.innerWidth - (minWidthProp ?? r.width) - 8)),
    top: flip ? r.top - GAP : r.bottom + GAP,
    minWidth: minWidthProp ?? r.width,
    flipped: flip,
  };
}

export function Popover({
  anchorRef,
  open,
  onClose,
  children,
  minWidth,
  className,
  role = "dialog",
  "aria-label": ariaLabel,
}: PopoverProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const [coords, setCoords] = useState<Coords | null>(null);

  // Position synchronously before paint to avoid a flash at (0,0).
  useLayoutEffect(() => {
    if (!open || !anchorRef.current) return;
    const reposition = () => {
      if (anchorRef.current) setCoords(computeCoords(anchorRef.current, minWidth));
    };
    reposition();
    window.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);
    return () => {
      window.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
    };
  }, [open, anchorRef, minWidth]);

  // Outside-click + Escape dismissal.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as Node;
      if (panelRef.current?.contains(target)) return;
      if (anchorRef.current?.contains(target)) return;
      onClose();
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener("pointerdown", onPointerDown, true);
    document.addEventListener("keydown", onKeyDown, true);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown, true);
      document.removeEventListener("keydown", onKeyDown, true);
    };
  }, [open, onClose, anchorRef]);

  if (!open || !coords || typeof document === "undefined") return null;

  return createPortal(
    <div
      ref={panelRef}
      className={className}
      role={role}
      aria-label={ariaLabel}
      style={{
        left: coords.left,
        top: coords.flipped ? undefined : coords.top,
        bottom: coords.flipped ? window.innerHeight - coords.top : undefined,
        minWidth: coords.minWidth,
      }}
    >
      {children}
    </div>,
    document.body,
  );
}
